/**
 * SafeHtml
 *
 * Renders model-generated / editor-provided HTML after running it through
 * DOMPurify. Deliverable section content (ADS, FRS, SDS, SDTM, IIS, ...) is
 * produced as raw HTML by the generation pipeline and can subsequently be
 * edited by editor-role users, so it is untrusted. Every place that previously
 * used `dangerouslySetInnerHTML` on that content now goes through this
 * component instead.
 *
 * The allow-list below was derived from what the generators actually emit
 * (functions/src/prompts/*Prompts.ts and functions/src/<doc>/<doc>Generator.ts):
 * headings, paragraphs, lists, tables, bold/italic, code, links, plus the
 * `class` / `data-*` hooks used by placeholder spans and requirement tables.
 * Anything outside it — <script>, event-handler attributes (onerror, onclick,
 * ...), <iframe>/<object>, inline styles, and javascript:/data: URLs — is
 * removed by DOMPurify.
 */
import DOMPurify from 'dompurify';

// Formatting tags the deliverable generators emit. Verified against the
// prompt formatting instructions and the requirement/inventory table builders.
export const ALLOWED_TAGS = [
  // headings & block-level text
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'br', 'hr', 'blockquote', 'pre', 'div', 'span',
  // lists
  'ul', 'ol', 'li', 'dl', 'dt', 'dd',
  // tables (requirement tables, inventory tables)
  'table', 'caption', 'colgroup', 'col', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
  // inline formatting
  'strong', 'b', 'em', 'i', 'u', 's', 'sub', 'sup', 'code', 'small', 'mark', 'abbr',
  // links
  'a',
];

// Attributes the generators emit. data-* attributes (data-description,
// data-req-id, data-de-id, data-test-id, data-form-code) are permitted via
// ALLOW_DATA_ATTR rather than being listed individually.
export const ALLOWED_ATTR = [
  'class', 'id', 'title',
  'colspan', 'rowspan', 'scope',
  'href', 'target', 'rel',
];

// Harden links: any anchor that opens a new tab gets rel="noopener noreferrer"
// so a malicious href cannot reach back into window.opener (reverse tabnabbing).
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.getAttribute('target')) {
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

/**
 * Sanitize an untrusted HTML string down to the deliverable formatting set.
 *
 * Strips <script>, event-handler attributes, and javascript:/data: URLs while
 * keeping legitimate formatting. Exported so non-React callers (e.g. a
 * verification script) can reuse the exact same configuration.
 */
export function sanitizeHtml(dirty: string): string {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: true,
    // ALLOWED_URI_REGEXP is left at the DOMPurify default, which already blocks
    // javascript:/data: navigation while permitting http(s)/mailto/tel and
    // relative or fragment links.
  });
}

interface SafeHtmlProps {
  /** Untrusted HTML (model-generated or editor-edited deliverable content). */
  html: string | null | undefined;
  /** Optional class names applied to the wrapping <div>. */
  className?: string;
}

/**
 * Drop-in replacement for
 *   <div className={...} dangerouslySetInnerHTML={{ __html: content }} />
 * that sanitizes `html` before rendering it.
 */
export default function SafeHtml({ html, className }: SafeHtmlProps) {
  return (
    <div
      className={className}
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(html ?? '') }}
    />
  );
}
